import { GetChannelMarkerTypesResponse } from 'api/markerTypes';
import { StainType } from 'interfaces/stainType';
import { filter, includes } from 'lodash';
import { ChannelModality, getEffectiveChannelModality, getMarkerTypeStain } from 'utils/multiplex/channelModality';
import * as yup from 'yup';

type ChannelModalities = Record<string, string> | undefined;

const hasChannelModality = (
  channelName: string,
  modality: ChannelModality,
  stainTypeOptions: StainType[],
  channelModalities: ChannelModalities
) => getEffectiveChannelModality({ markerType: channelName, stainTypeOptions, channelModalities }) === modality;

export const getSelectedSlideChannelMarkers = (channelMarkersResponse?: GetChannelMarkerTypesResponse) =>
  channelMarkersResponse?.channelMarkerTypes || [];

export const getChannelMarkersInAllSlides = (channelMarkersResponse?: GetChannelMarkerTypesResponse) => {
  const channelMarkers = getSelectedSlideChannelMarkers(channelMarkersResponse);

  return filter(channelMarkers, (channelMarker) => {
    return channelMarkersResponse?.channelAppearance[channelMarker] === channelMarkersResponse?.totalSlidesCount;
  });
};

export const getNonDnaChannelNames = (
  channelMarkers: string[],
  stainTypeOptions: StainType[] = [],
  channelModalities?: ChannelModalities
) =>
  filter(
    channelMarkers,
    (channelMarker) => !hasChannelModality(channelMarker, 'dna', stainTypeOptions, channelModalities)
  );

export const getDnaChannelNames = (
  channelMarkers: string[],
  stainTypeOptions: StainType[] = [],
  channelModalities?: ChannelModalities
) =>
  filter(channelMarkers, (channelMarker) =>
    hasChannelModality(channelMarker, 'dna', stainTypeOptions, channelModalities)
  );

export const getRnaProbeChannelNames = (
  channelMarkers: string[],
  stainTypeOptions: StainType[] = [],
  channelModalities?: ChannelModalities
) =>
  filter(channelMarkers, (channelMarker) =>
    hasChannelModality(channelMarker, 'rna', stainTypeOptions, channelModalities)
  );

export const getMifChannelNames = (
  channelMarkers: string[],
  stainTypeOptions: StainType[] = [],
  channelModalities?: ChannelModalities
) =>
  filter(channelMarkers, (channelMarker) =>
    hasChannelModality(channelMarker, 'mif', stainTypeOptions, channelModalities)
  );

// mIF channels whose stain is nuclear-localized: the explicit nuclear segmentation fallback.
export const getNuclearMifChannelNames = (
  channelMarkers: string[],
  stainTypeOptions: StainType[] = [],
  channelModalities?: ChannelModalities
) =>
  filter(getMifChannelNames(channelMarkers, stainTypeOptions, channelModalities), (channelMarker) =>
    Boolean(getMarkerTypeStain(channelMarker, stainTypeOptions)?.nucleus)
  );

export const getStainKindChannelGroupLabel = (
  channelName: string,
  stainTypeOptions: StainType[] = [],
  channelModalities?: ChannelModalities
) => (hasChannelModality(channelName, 'rna', stainTypeOptions, channelModalities) ? 'RNA probes' : 'Markers');

export const buildSelectedSlideChannelSchema = (
  allowedChannelNames: string[],
  {
    inputLabel = 'Channel',
    getDisplayName = (value: string) => value,
  }: {
    inputLabel?: string;
    getDisplayName?: (value: string) => string;
  } = {}
) =>
  yup
    .array()
    .of(yup.string())
    .test('is-valid-selected-slide-channel', 'Unknown selected-slide channel', function (value) {
      const { path, createError } = this;

      if (!value) {
        return true;
      }

      for (let index = 0; index < value.length; index += 1) {
        if (!includes(allowedChannelNames, value[index])) {
          return createError({
            path,
            message: `${inputLabel}: ${getDisplayName(value[index])} is not available on the selected slides`,
          });
        }
      }

      return true;
    })
    .required();

export const buildCohortWideChannelSchema = (
  channelMarkersInAllSlides: string[],
  {
    inputLabel = 'Channel',
    getDisplayName = (value: string) => value,
  }: {
    inputLabel?: string;
    getDisplayName?: (value: string) => string;
  } = {}
) =>
  yup
    .array()
    .of(yup.string())
    .test('is-valid-channel', 'Partial Cohort Channel', function (value) {
      const { path, createError } = this;

      if (!value) {
        return true;
      }

      for (let index = 0; index < value.length; index += 1) {
        if (!channelMarkersInAllSlides.includes(value[index])) {
          return createError({
            path,
            message: `${inputLabel}: ${getDisplayName(value[index])} does not appear in all selected slides`,
          });
        }
      }

      return true;
    })
    .required();

export const getCohortWideAllowedChannelNames = (channelMarkersInAllSlides: string[], allowedChannelNames: string[]) =>
  filter(channelMarkersInAllSlides, (channelMarker) => includes(allowedChannelNames, channelMarker));
